import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, History, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { HistoryTimeline } from "@/components/HistoryTimeline";
import { CrateForm, CrateStatusForm } from "@/components/forms/CrateForms";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-sm">{value ?? "—"}</p>
    </div>
  );
}

export default function CrateDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isStaff = user?.role !== "client_user";
  const isAdmin = user?.role === "admin";
  const [crate, setCrate] = useState(null);
  const [events, setEvents] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [archiving, setArchiving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [archiveError, setArchiveError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, h, cl] = await Promise.all([
        api.get(`/crates/${id}`),
        api.get(`/crates/${id}/history`),
        isStaff ? api.get("/clients") : Promise.resolve({ data: [] }),
      ]);
      setCrate(c.data); setEvents(h.data);
      setClients((cl.data || []).filter((x) => !x.is_archived));
    } catch (err) {
      if (err?.response?.status === 404) navigate("/crates");
    } finally { setLoading(false); }
  }, [id, isStaff, navigate]);

  useEffect(() => { load(); }, [load]);

  async function toggleArchive() {
    if (!crate) return;
    setArchiving(true); setArchiveError(null);
    try {
      await api.post(`/crates/${crate.id}/archive`, { archived: !crate.is_archived });
      setConfirmOpen(false);
      await load();
    } catch (err) { setArchiveError(apiError(err)); } finally { setArchiving(false); }
  }

  if (loading || !crate) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-6">
      <Link to="/crates" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back to Wooden Crates</Link>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold tracking-tight">{crate.crate_code}</h2>
            <StatusBadge status={crate.status} />
            {crate.is_archived && <Badge variant="secondary">Archived</Badge>}
          </div>
          <p className="text-muted-foreground">Wooden Crate · {crate.client_name} · {crate.reuse_count} round trip{crate.reuse_count === 1 ? "" : "s"}</p>
        </div>
        {isStaff && (
          <div className="flex flex-wrap gap-2">
            <CrateStatusForm crateId={crate.id} currentStatus={crate.status} onSaved={load} />
            <CrateForm clients={clients} crate={crate} onSaved={load} />
            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setConfirmOpen(true); setArchiveError(null); }}
                disabled={archiving}
                data-testid={crate.is_archived ? "restore-crate-btn" : "archive-crate-btn"}
              >
                {crate.is_archived ? "Restore" : "Archive"}
              </Button>
            )}
          </div>
        )}
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={(o) => { if (!archiving) { setConfirmOpen(o); if (!o) setArchiveError(null); } }}>
        <AlertDialogContent data-testid="archive-crate-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{crate.is_archived ? "Restore this crate?" : "Archive this crate?"}</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium text-slate-900">{crate.crate_code}</span>
              {crate.client_name ? <> · {crate.client_name}</> : null}
              {crate.is_archived
                ? " will be moved back to the active Wooden Crates list."
                : " will be hidden from the active Wooden Crates list. You can restore it later from the Archived Crates page."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {archiveError && <p className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">{archiveError}</p>}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archiving} data-testid="archive-crate-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); toggleArchive(); }}
              disabled={archiving}
              data-testid="archive-crate-confirm"
            >
              {archiving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              {crate.is_archived ? "Yes, restore" : "Yes, archive"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Card>
        <CardHeader><CardTitle className="text-base">Crate Details</CardTitle></CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Crate ID" value={crate.crate_code} />
          <Field label="Organization" value={crate.client_name} />
          <Field label="Cardboard Box Inside" value={crate.has_cardboard_box ? (crate.cardboard_condition === "Good" ? "Yes — Good" : `Yes — ${crate.cardboard_condition}`) : "No"} />
          <Field label="Current Location" value={crate.current_location} />
          <Field label="Product Inside" value={crate.product ? <Link to={`/products/${crate.product_id}`} className="text-primary hover:underline">{crate.product.serial_number}</Link> : "Empty"} />
          <Field label="Dedicated DSK" value={crate.dedicated ? <Link to={`/products/${crate.dedicated.id}`} className="text-primary hover:underline">{crate.dedicated.serial_number}</Link> : "Not paired yet — set on first shipment"} />
          <Field label="Last Shipment" value={crate.last_shipment ? <Link to={`/shipments/${crate.last_shipment_id}`} className="text-primary hover:underline">{crate.last_shipment.shipment_code}</Link> : null} />
          <Field label="Round Trips" value={crate.reuse_count} />
          <Field label="Outbound Tracking" value={crate.outbound_tracking} />
          <Field label="Return Tracking" value={crate.return_tracking} />
          <Field label="Created" value={new Date(crate.created_at).toLocaleDateString()} />
          {crate.notes && (<div className="sm:col-span-2 lg:col-span-3"><Field label="Notes" value={crate.notes} /></div>)}
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><History className="h-4 w-4" /> History Timeline</CardTitle></CardHeader>
        <CardContent><HistoryTimeline events={events} /></CardContent>
      </Card>
    </div>
  );
}
